import type { HomeyAPI, Driver } from 'homey-api';

import { BaseApiStore, type FetchArgs } from '../BaseApiStore.ts';

export type DriverProps = {
  [K in keyof Driver as Driver[K] extends Function ? never : K]: Driver[K];
};

type DriversData = { [key: string]: Driver };
type DriversById = { [key: string]: DriverProps };

interface State {
  data: DriversData | null;
  byId: DriversById | null;
  isLoading: boolean;
  error: unknown;
}

export type DriversStoreState = State;

export class DriversStore extends BaseApiStore<State, Driver> {
  constructor() {
    super('drivers');
  }

  override createInitialState() {
    return {
      data: null,
      byId: null,
      isLoading: true,
      error: null,
    };
  }

  override async fetch({ api, silent, cache, abortController }: FetchArgs) {
    if (silent === false) {
      this.set(this.createInitialState());
    }

    try {
      await this.connectManager({ api, manager: api.drivers, reference: this });

      if (abortController.signal.aborted) return;

      // @ts-ignore
      const driversPromise = api.drivers.getDrivers({ $cache: cache });

      const [driversData] = await Promise.all([driversPromise]);

      if (abortController.signal.aborted) return;

      const drivers = Object.values(driversData);

      const data: DriversData = {};
      const byId: DriversById = {};

      for (const driverInstance of drivers) {
        const driverReference = { ...driverInstance };

        data[driverReference.id] = driverInstance;
        byId[driverReference.id] = driverReference;
      }

      this.set({
        ...this.createInitialState(),
        data: data,
        byId: byId,
        isLoading: false,
      });
    } catch (error) {
      if (abortController.signal.aborted) return;

      this.set({
        ...this.createInitialState(),
        isLoading: false,
        error,
      });
    }
  }

  override destroy(reason: string) {
    try {
      const api = this.getApi();
      this.disconnectManager({ api, manager: api.drivers, reference: this }).catch(console.error);
    } catch (error) {}
  }

  override onAttached({ api }: { api: HomeyAPI }): void {
    api.drivers.on('driver.create', this.handleCreate);
    api.drivers.on('driver.update', this.handleUpdate);
    api.drivers.on('driver.delete', this.handleDelete);
  }

  override onDetached({ api }: { api: HomeyAPI }): void {
    api.drivers.off('driver.create', this.handleCreate);
    api.drivers.off('driver.update', this.handleUpdate);
    api.drivers.off('driver.delete', this.handleDelete);
  }

  getInstanceFromCache(driver: { id: string }) {
    return this.getCache()[driver.id];
  }

  getCache(): { [key: string]: Driver } {
    const api = this.getApi();
    // @ts-ignore
    const cache = api.drivers.__cache['driver'];
    return cache;
  }

  handleCreate = (createdDriver: Driver) => {
    this.DEBUG && console.info(`create:driver:${createdDriver.id}`);
    const state = this.get();
    const driverInstance = this.getInstanceFromCache(createdDriver);

    if (driverInstance == null) return;

    const driverReference = { ...driverInstance };

    this.set({
      data: {
        ...state.data,
        [createdDriver.id]: driverInstance,
      },
      byId: {
        ...state.byId,
        [createdDriver.id]: driverReference,
      },
    });
  };

  handleUpdate = (updatedDriver: Driver) => {
    this.DEBUG && console.info(`update:driver:${updatedDriver.id}`);

    this.addToQueue(updatedDriver.id, updatedDriver);

    this.batch((updateQueue) => {
      const state = this.get();

      const nextById = {
        ...state.byId,
      };

      let updateCount = 0;

      for (const [id, queuedDriver] of Object.entries(updateQueue)) {
        const driverInstance = this.getInstanceFromCache(queuedDriver);

        if (state.data == null) {
          this.DEBUG && console.info(`update:driver:missing-data`);
          continue;
        }

        if (driverInstance == null || state.data[id] == null) {
          this.DEBUG && console.info(`update:driver:unknown:${id}`);
          continue;
        }

        const driverReference = { ...driverInstance };

        nextById[id] = driverReference;

        updateCount++;
      }

      this.DEBUG && console.info(`update:driver:batch:count:${updateCount}`);

      updateCount > 0 &&
        this.set({
          byId: nextById,
        });
    });
  };

  handleDelete = (deletedDriver: Driver) => {
    this.DEBUG && console.info(`delete:driver:${deletedDriver.id}`);
    const state = this.get();

    if (state.data == null) {
      this.DEBUG && console.log(`delete:driver:missing-data`);
      return;
    }

    const driverInstance = state.data[deletedDriver.id];

    if (driverInstance == null) return;

    const nextData = { ...state.data };
    const nextById = { ...state.byId };

    delete nextData[driverInstance.id];
    delete nextById[driverInstance.id];

    this.set({
      data: nextData,
      byId: nextById,
    });
  };

  restore() {
    const cache = this.getCache();
    const drivers = Object.values(cache ?? {});
    const data: DriversData = {};
    const byId: DriversById = {};

    for (const driverInstance of drivers) {
      const driverReference = { ...driverInstance };
      data[driverReference.id] = driverInstance;
      byId[driverReference.id] = driverReference;
    }

    this.set({
      data: data,
      byId: byId,
    });
  }
}

export const driversStore = new DriversStore();
